import React, { useEffect, useState } from 'react'
import { gql } from '@apollo/client'
import { createGqlClient } from '../../graphql-client.js'
import './EquipmentOverview.css'

export interface DisplayEquipment {
    id: string
    name: string
    isOnline: boolean
    isRelayOn: boolean
    isUnderMaintenance: boolean
}

export function equipmentStatus(equipment: DisplayEquipment) {
    if (!equipment.isOnline) return { kind: 'maintenance', label: 'MAINTENANCE', reason: 'Offline' }
    if (equipment.isUnderMaintenance)
        return { kind: 'maintenance', label: 'MAINTENANCE', reason: '' }
    if (equipment.isRelayOn) return { kind: 'unavailable', label: 'IN USE', reason: '' }
    return { kind: 'available', label: 'AVAILABLE', reason: '' }
}

const query = gql`
    query EquipmentDisplay($from: ID) {
        equipment(count: 100, from: $from) {
            id name isOnline isRelayOn isUnderMaintenance
        }
    }
`

export default function EquipmentOverview() {
    const [equipment, setEquipment] = useState<DisplayEquipment[]>([])
    const [updated, setUpdated] = useState<Date | null>(null)
    const [error, setError] = useState('')

    useEffect(() => {
        const client = createGqlClient()
        let disposed = false
        let timer: ReturnType<typeof setTimeout>
        async function refresh() {
            try {
                const items: DisplayEquipment[] = []
                const seen = new Set<string>()
                let from: string | null = null
                while (true) {
                    const result = await client.query<{ equipment: DisplayEquipment[] }>({
                        query, variables: { from }, fetchPolicy: 'network-only'
                    })
                    if (disposed) return
                    const batch = result.data.equipment
                    const unseen = batch.filter(item => !seen.has(item.id))
                    unseen.forEach(item => { seen.add(item.id); items.push(item) })
                    // Teacher accounts return their complete permitted list, ignoring the cursor.
                    if (batch.length < 100 || unseen.length === 0) break
                    from = batch[batch.length - 1].id
                }
                if (!disposed) {
                    // Opt-in preview only; no demonstration equipment is stored in the database.
                    if (window.statusBoardMaintenancePreview) {
                        items.push({ id: 'maintenance-preview', name: 'Sawstop #3',
                            isOnline: true, isRelayOn: false, isUnderMaintenance: true })
                    }
                    setEquipment(items.filter(item => item.isOnline).sort((a, b) =>
                        a.name.localeCompare(b.name)))
                    setUpdated(new Date())
                    setError('')
                }
            } catch {
                if (!disposed) setError('Unable to refresh — displayed statuses may be outdated. Retrying automatically.')
            } finally {
                if (!disposed) timer = setTimeout(refresh, 30000)
            }
        }
        refresh()
        return () => { disposed = true; clearTimeout(timer); client.stop() }
    }, [])

    return <main className="equipment-overview">
        {error && <p className="overview-error" role="alert">{error}</p>}
        {!updated && !error && <p role="status">Loading equipment status…</p>}
        {updated && equipment.length === 0 && <p>No equipment to display.</p>}
        <div className="overview-grid" role="list" aria-label="Equipment status">
            {equipment.map(item => {
                const status = equipmentStatus(item)
                return <div role="listitem" className={`overview-equipment ${status.kind}`} key={item.id}>
                    <div className="overview-name">{item.name}</div>
                    <div><strong className="overview-status">{status.label}</strong>{status.reason && <span className="overview-reason">{status.reason}</span>}</div>
                </div>
            })}
        </div>
        {updated && <footer className="overview-footer">
            <time dateTime={updated.toISOString()}>
                Last updated: {new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(updated)}
            </time>
        </footer>}
    </main>
}
